/**
 * queues-api — typed wrappers for the Queuing data layer, through the
 * @v0y4ge/api-client `apiRaw` choke point (same seam as wf-api / REQ-034).
 *
 * The queue operations are DECLARED (spec: specs/queuing-seed-package-2026-07-17.md
 * + the 2026-07-17 changelog) and pending in @v0y4ge/api-contracts / api-mocks —
 * Claude Code owns that layer (REQ-038). Until the real ops land, the preview
 * harness serves them from CD preview seeds (`__source: "cd-preview-seed"`),
 * surfaced per screen via <DataSourceStrip>.
 */
import { apiRaw } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";
import type { QFFlow } from "./qf-model";

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

/* ── Types ──────────────────────────────────────────────────────────── */
export type QueueStatus = "open" | "paused" | "closed";
export type EntryStatus = "waiting" | "called" | "serving" | "done" | "left" | "no-show";
export type MsgChannel = "sms" | "whatsapp";

export interface QueueTypeCapability { id: string; label: string; on: boolean; }
export interface QueueType {
  id: string; label: string; blurb: string;
  /** Locked capability set — what a queue of this type can/can't do. */
  capabilities: QueueTypeCapability[];
  /** Editable preset a new queue is seeded with. */
  presets: { slaTargetMins: number; turnMessages: number; bookingQuestions: boolean; autoCheckout: boolean; resourceTracking: boolean };
}

export interface QueueHours { days: number[]; start: string; end: string; } // days: 0=Mon … 6=Sun; "HH:MM" wall-clock
export interface QueueStaffRef {
  id: string; name: string; role?: string | null;
  /** Actively serving this queue right now. Absent = true (legacy records). */
  onQueue?: boolean;
  /** True when the backend auto-took them off for a calendar conflict (autoOffOnConflict). */
  autoOff?: boolean;
  /** Upcoming/current calendar conflict (appointment etc.) — surfaces a "take off queue" suggestion in the console. */
  conflict?: { title: string; start: string; end: string } | null;
}
/** Ordered per-queue catalog selection: a category item is a LIVE group (its
 *  services update with the catalog); a service item is an individual pick. */
export interface QueueServiceSel { type: "category" | "service"; id: string; }

export interface QueueSummary {
  id: string; name: string; typeId: string; status: QueueStatus;
  locationId: string; locationName: string;
  services: string[]; joinCode: string;
  /** @deprecated LEGACY — shared-journey reference, superseded by the embedded
   *  `journey` (2026-07-21). Kept for backfill only; new writes send null. */
  flowId: string | null;
  /** THIS QUEUE'S embedded journey (2026-07-21 operator decision: the shared
   *  Queue Journeys library is retired). Seeded once (starter / copy / blank),
   *  then owned and edited per queue. When present it supersedes flowId
   *  everywhere, including the public journey served by getPublicQueue. */
  journey?: QFFlow | null;
  /** Human label for where the journey was seeded from (badge in the wizard). */
  journeySource?: string;
  /** Hero image (URL / data URL) shown at the top of every queue-flow page.
   *  Absent/null = the brand gradient. */
  heroImage?: string | null;
  /** Ticket prefix — prefix + the customer's join number = their ticket (e.g. "A-104"). */
  ticketPrefix?: string;
  /** Ordered catalog selection driving `services` (resolved server-side, live). */
  serviceSelection?: QueueServiceSel[];
  /** @deprecated LEGACY — folded into the embedded `journey` on first edit (2026-07-21). */
  pageOverrides?: Record<string, boolean>;
  /** @deprecated LEGACY — folded into the embedded `journey` on first edit (2026-07-21). */
  pageOverrideCfgs?: Record<string, Record<string, unknown>>;
  /** Services temporarily marked unavailable on the live queue (subset of `services`). */
  unavailableServices?: string[];
  /** Resources (desk / room / equipment) temporarily out of action (subset of `resources`). */
  unavailableResources?: string[];
  /** Auto-take staff off queue while a calendar conflict is running, back on after. Absent = true. */
  autoOffOnConflict?: boolean;
  slaTargetMins: number; staffed: number; needed: number;
  /** Weekly opening windows — e.g. Mon 10–14 and Thu–Sat 9–17. Empty = follows location hours. */
  hours: QueueHours[];
  assignedStaff: QueueStaffRef[];
  resources: string[];
  waiting: number; serving: number; avgWaitMins: number; longestWaitMins: number;
  servedToday: number;
}

export interface QueueEntry {
  id: string; name: string; phone: string; channel: MsgChannel;
  party: number; service: string | null; status: EntryStatus;
  joinedAt: string; calledAt?: string | null; servedAt?: string | null; completedAt?: string | null;
  waitMins: number; serviceMins: number | null; position: number;
  priority: boolean; source: "qr" | "kiosk" | "staff" | "online";
  /** Set when a re-added customer had already been called (no-show / left after a call) —
   *  surfaces a "Called before" marker so staff have context. */
  calledBefore?: boolean;
  resource?: string | null; notes?: string | null;
  /** Optional staff preference set when staff add a walk-up (e.g. a language
   *  requirement a specific staffer can meet). Never required. */
  preferredStaff?: { id: string; name: string } | null;
  answers?: { q: string; a: string }[]; unread: number; calls: number;
  /** The customer's ticket: queue prefix + their number when they joined (e.g. "A-104"). */
  ticket?: string | null;
}

export interface QueueBoard {
  asOf: string; queue: QueueSummary; entries: QueueEntry[];
  stats: { waiting: number; avgWaitMins: number; longestWaitMins: number; servedToday: number; avgServiceMins: number };
}

export interface QueueMessage { id: string; dir: "in" | "out"; text: string; at: string; channel: MsgChannel; auto: boolean; by?: string | null; }

export interface QueueFlowRecord {
  id: string; name: string; typeId: string | null; desc: string;
  trigger: QFFlow["trigger"]; steps: QFFlow["steps"]; usedBy: number; updatedAt: string;
}

export interface QueueHealthAlert { id: string; severity: "critical" | "warn" | "info"; kind: string; title: string; body: string; at: string; }
export interface QueueHealth {
  range: string; locationId: string | null; locationName: string;
  kpis: {
    avgWaitMins: number; p90WaitMins: number; slaTargetMins: number;
    avgServiceMins: number; forecastServiceMins: number;
    abandonPct: number; csat: number; served: number;
  };
  trend: { t: string; wait: number }[];
  byService: { service: string; served: number; avgServiceMins: number; forecastMins: number }[];
  byStaff: { name: string; served: number; avgServiceMins: number; teamAvgMins: number; csat: number }[];
  alerts: QueueHealthAlert[];
}

export interface QueueRecommendation { summary: string; actions: string[]; confidence: "high" | "medium" | "low"; basedOn: string; }

/* ── Queue dashboard (location / region / org performance, 2026-07-22) ── */
export type QDashScope = "location" | "region" | "org";
/** One hour of queue activity — joins / served / left counts. */
export interface QDashPoint { t: string; joins: number; served: number; left: number; }
export interface QDashTimesPoint { t: string; waitMins: number; serviceMins: number; }
export interface QDashKpis { avgWaitMins: number; inQueue: number; serving: number; staffAvailable: number; completed: number; dropoffs: number; }
/** Performance-table row: a queue (location scope), a location (region scope) or a region (org scope). */
export interface QDashRow extends QDashKpis { id: string; name: string; kind: "queue" | "location" | "region"; meta: string; avgServiceMins: number; }
export interface QDashEntityTimes { id: string; name: string; avgWaitMins: number; maxWaitMins: number; avgServiceMins: number; maxServiceMins: number; }
export interface QueueDashboard {
  range: string; scope: QDashScope;
  scopeName: string;
  /** Resolved selection (server defaults when the request omitted them). */
  locationId: string | null; regionId: string | null;
  regions: { id: string; name: string }[];
  locations: { id: string; name: string; regionId: string }[];
  /** Queues of the scoped location (location scope only; empty otherwise). */
  queues: { id: string; name: string; locationId: string }[];
  /** Region/org: time-based metrics AVERAGED across locations, volume metrics SUMMED. */
  kpis: QDashKpis;
  rows: QDashRow[];
  /** Trending series — per queue (location), per location (region), per region (org). */
  trending: { id: string; label: string; points: QDashPoint[] }[];
  /** Wait vs service time by hour at the current scope. */
  times: QDashTimesPoint[];
  waitService: { avgWaitMins: number; maxWaitMins: number; avgServiceMins: number; maxServiceMins: number; byEntity: QDashEntityTimes[] };
}
export const getQueueDashboard = (params: { range: string; scope: QDashScope; locationId?: string | null; regionId?: string | null }) =>
  tag("getQueueDashboard", apiRaw<QueueDashboard>(`/v1/admin/queues/dashboard${q({ range: params.range, scope: params.scope, locationId: params.locationId, regionId: params.regionId })}`, undefined, 'getQueueDashboard'));

/* Display boards — public "now serving" displays. */
export interface MessageBoard {
  id: string; name: string; queueIds: string[]; locationId: string | null;
  layout: "now-serving" | "full-list"; theme: "light" | "dark";
  showWaitEstimates: boolean; message: string;
  /** How queuers are identified on the display. Default "first-initial" (privacy-shortened). */
  nameFormat?: "full" | "first-initial" | "first" | "ticket";
  /** Ordered content blocks composing each queue card (QueueBoardBuilder). Absent = layout-derived defaults. */
  blocks?: { uid: string; type: string; cfg: Record<string, unknown> }[];
  updatedAt: string;
}
export interface PublicDisplayQueue {
  id: string; name: string; nowServing: { name: string; ticket?: string | null; position: number | null } | null;
  called: { name: string; ticket?: string | null; position: number }[]; waiting: number; estWaitMins: number;
  /** Friendly opening-hours label for the display, e.g. "Mon–Fri 9 AM–5 PM". */
  hoursLabel?: string;
  /** Waiting customers next in line (privacy-shortened client-side) — full-list boards show them. */
  upNext?: { name: string; ticket?: string | null; position: number }[];
  /** Live per-service load: services currently represented in the queue with active-entry counts. */
  services?: { name: string; waiting: number }[];
}
export interface PublicDisplay { board: MessageBoard; org: string; asOf: string; queues: PublicDisplayQueue[]; }

/* Archive — every queuer across the admin's active locations. */
export interface ArchiveEntry {
  id: string; name: string; phone: string; channel: MsgChannel;
  queueId: string; queueName: string; locationId: string; locationName: string;
  service: string | null; party: number; date: string; joinedAt: string;
  waitMins: number; serviceMins: number | null;
  outcome: "served" | "left" | "no-show" | "removed" | "transferred";
  source: "qr" | "kiosk" | "staff" | "online"; messages: number;
}

export interface PublicQueueView {
  queue: {
    name: string; org: string; status: QueueStatus; waiting: number; estWaitMins: number; services: string[]; askQuestions: boolean;
    /** Queue hero image — tops every journey page; absent = brand gradient. */
    heroImage?: string | null;
    /** The queue's selection resolved into ordered groups — category picks render
     *  as titled sections on the Service-picker page. */
    serviceGroups?: { label: string | null; services: string[] }[];
    /** The journey's designed pages, in order (cfg from the queue-flow's page steps;
     *  contact cfg arrives with `resolvedQuestions` — backend resolves library refs). */
    pages?: { page: string; cfg: Record<string, unknown> }[];
    /** Live estimate per service — the Wait Time page shows the picked service's number. */
    serviceWaits?: Record<string, number>;
    /** Optional Location-picker page: nearby locations, closest first (≤10),
     *  each with per-service waits so the page shows the picked service's number. */
    nearbyLocations?: { id: string; name: string; distanceKm: number; estWaitMins?: number; serviceWaits?: Record<string, number> }[];
  };
  entry?: {
    id: string; name: string; position: number; status: EntryStatus; estWaitMins: number; channel: MsgChannel;
    /** The customer's ticket (queue prefix + join number). */
    ticket?: string | null;
    /** Customer-visible thread (present when the journey's Waiting page allows messaging). */
    messages?: { from: "you" | "team"; text: string; at: string }[];
  } | null;
  /** CSAT survey for the journey's How-did-we-do page — resolved server-side; present when entry is done. */
  survey?: { id: string; name: string; questions: { id: string; label: string; type: "rating" | "text" }[]; submitted: boolean } | null;
}

/* ── QueueSummary normalization ────────────────────────────────────────
 * The real worker'''s `queues` table (workers/queue/migrations/ops/0001_baseline.sql)
 * only carries id/company_id/service_id/location_id/name/status/mode/
 * settings_json/timestamps — a small subset of the rich QueueSummary shape
 * the console/list/health/wizard screens read. Rather than scatter `?? `
 * guards across every screen (and risk missing one — see the queue console
 * crash on `board.queue.slaTargetMins`), every QueueSummary-shaped response
 * is normalized HERE, once, at the data boundary: every field the worker
 * doesn'''t yet enrich gets an honest empty/zero default (never a fabricated
 * value) so downstream screens can read any QueueSummary field unguarded
 * without crashing. As the worker fills in more real fields, the `??`
 * fallbacks below simply stop being exercised. */
/** The queue ROW as the worker actually serves it: the settings blob it stores
 *  in `settings_json` is parsed and returned as `settings`, and the foreign
 *  keys keep their column names. Every studio-facing field the screens read
 *  (typeId / services / assignedStaff / slaTargetMins / staffed / hours / …)
 *  lives INSIDE that blob, not at the top level — reading only the top level is
 *  why the queue list rendered "quick / 0-0 / follows location hours" for every
 *  queue regardless of what was configured. */
type QueueRowWire = Partial<QueueSummary> & {
  id: string; name: string; status: QueueStatus;
  location_id?: string | null;
  service_id?: string | null;
  settings?: (Partial<QueueSummary> & { typeLabel?: string }) | null;
};

function normalizeQueueSummary(raw: QueueRowWire): QueueSummary {
  const cfg = (raw.settings ?? {}) as Partial<QueueSummary>;
  return {
    id: raw.id,
    name: raw.name,
    typeId: raw.typeId ?? cfg.typeId ?? "quick",
    status: raw.status,
    locationId: raw.locationId ?? raw.location_id ?? "",
    locationName: raw.locationName ?? "",
    services: raw.services ?? cfg.services ?? [],
    joinCode: raw.joinCode ?? cfg.joinCode ?? "",
    flowId: raw.flowId ?? cfg.flowId ?? null,
    journey: raw.journey ?? cfg.journey ?? null,
    journeySource: raw.journeySource ?? cfg.journeySource,
    heroImage: raw.heroImage ?? cfg.heroImage ?? null,
    ticketPrefix: raw.ticketPrefix ?? cfg.ticketPrefix ?? "",
    serviceSelection: raw.serviceSelection ?? cfg.serviceSelection,
    pageOverrides: raw.pageOverrides ?? cfg.pageOverrides,
    pageOverrideCfgs: raw.pageOverrideCfgs ?? cfg.pageOverrideCfgs,
    unavailableServices: raw.unavailableServices ?? cfg.unavailableServices ?? [],
    unavailableResources: raw.unavailableResources ?? cfg.unavailableResources ?? [],
    autoOffOnConflict: raw.autoOffOnConflict ?? cfg.autoOffOnConflict,
    slaTargetMins: raw.slaTargetMins ?? cfg.slaTargetMins ?? 15,
    staffed: raw.staffed ?? cfg.staffed ?? 0,
    needed: raw.needed ?? cfg.needed ?? 0,
    hours: raw.hours ?? cfg.hours ?? [],
    assignedStaff: raw.assignedStaff ?? cfg.assignedStaff ?? [],
    resources: raw.resources ?? cfg.resources ?? [],
    waiting: raw.waiting ?? 0,
    serving: raw.serving ?? 0,
    avgWaitMins: raw.avgWaitMins ?? 0,
    longestWaitMins: raw.longestWaitMins ?? 0,
    servedToday: raw.servedToday ?? 0,
  };
}

/* ── Queue board (Concierge) ─────────────────────────────────────────────
 * `GET /v1/admin/queues/{queueId}/board` answers the contract's
 * `QueueBoardResponse` (packages/api-contracts/src/queue.ts): a `{ data: … }`
 * envelope holding `queue` (the raw row), snake_case `entries` / `recent`
 * wires, `policy` and `summary`. This module used to hand that response
 * straight to the screen as if it were already a `QueueBoard`, so `board.queue`
 * was `undefined`, `normalizeQueueSummary` threw
 * "Cannot read properties of undefined (reading id)", and Concierge rendered
 * the pending-data-layer panel for every queue. Unwrap and map here — the same
 * boundary `listQueues` already normalizes at.
 */
const BOARD_STATUS: Record<string, EntryStatus> = {
  waiting: "waiting",
  called: "called",
  being_served: "serving",
  serving: "serving",
  completed: "done",
  done: "done",
  no_show: "no-show",
  left: "left",
};

interface BoardEntryWire {
  id: string; ticket_number?: string | null; customer_name?: string | null;
  customer_phone?: string | null; service_id?: string | null; service_name?: string | null;
  status?: string; position?: number; party_size?: number | null; priority?: boolean;
  notes?: string | null; joined_at?: string | null; called_at?: string | null;
  served_at?: string | null; completed_at?: string | null; wait_minutes?: number | null;
  service_minutes?: number | null;
}

/** Honest defaults only: a field the board wire has no column for stays empty /
 *  zero / null rather than being invented (no fabricated data). */
function normalizeBoardEntry(raw: BoardEntryWire): QueueEntry {
  return {
    id: raw.id,
    name: raw.customer_name ?? "",
    phone: raw.customer_phone ?? "",
    channel: "sms",
    party: raw.party_size ?? 1,
    service: raw.service_name ?? raw.service_id ?? null,
    status: BOARD_STATUS[raw.status ?? ""] ?? "waiting",
    joinedAt: raw.joined_at ?? "",
    calledAt: raw.called_at ?? null,
    servedAt: raw.served_at ?? null,
    completedAt: raw.completed_at ?? null,
    waitMins: Number.isFinite(raw.wait_minutes) ? Number(raw.wait_minutes) : 0,
    serviceMins: Number.isFinite(raw.service_minutes) ? Number(raw.service_minutes) : null,
    position: raw.position ?? 0,
    priority: raw.priority === true,
    source: "online",
    resource: null,
    notes: raw.notes ?? null,
    unread: 0,
    calls: raw.called_at ? 1 : 0,
    ticket: raw.ticket_number ?? null,
  };
}

interface QueueBoardWire {
  data?: {
    queue?: QueueRowWire;
    entries?: BoardEntryWire[];
    recent?: BoardEntryWire[];
    summary?: Record<string, number | undefined>;
  };
}

function normalizeQueueBoard(wire: QueueBoardWire): QueueBoard {
  const body = wire?.data ?? {};
  const summary = body.summary ?? {};
  const num = (value: number | undefined): number => (Number.isFinite(value) ? Number(value) : 0);
  // The console derives its "Recently finished" lane by filtering `entries` for
  // done/no-show/left, so the board separate `recent` wire is folded in here
  // rather than dropped (which is what made that lane permanently empty).
  const entries = [...(body.entries ?? []), ...(body.recent ?? [])].map(normalizeBoardEntry);
  return {
    asOf: new Date().toISOString(),
    queue: normalizeQueueSummary({
      ...(body.queue as QueueRowWire),
      waiting: num(summary.waiting),
      serving: num(summary.being_served),
      longestWaitMins: num(summary.longest_wait_minutes),
      servedToday: num(summary.completed_today),
    }),
    entries,
    stats: {
      waiting: num(summary.waiting),
      avgWaitMins: num(summary.estimated_wait_minutes),
      longestWaitMins: num(summary.longest_wait_minutes),
      servedToday: num(summary.completed_today),
      avgServiceMins: num(summary.avg_service_minutes),
    },
  };
}

/* ── Helpers ────────────────────────────────────────────────────────── */
const q = (params: Record<string, string | undefined | null>) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) sp.set(k, v); });
  const s = sp.toString();
  return s ? `?${s}` : "";
};
const json = (body: unknown): RequestInit => ({ method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

/* ── Queue setup ────────────────────────────────────────────────────── */
/** Envelope matches the contract's QueueListResponse (packages/api-contracts/src/queue.ts) —
 *  `{ data, pagination }`, NOT `{ queues }`. Confirmed against the real worker's
 *  `paginatedResponse()` (workers/_shared/pagination.ts) as served by GET /v1/admin/queues. */
export interface QueueListPage {
  data: QueueSummary[];
  pagination: { page: number; limit: number; total: number; hasMore: boolean };
}
export const listQueues = (locationId?: string | null) =>
  tag("listQueues", apiRaw<QueueListPage>(`/v1/admin/queues${q({ locationId })}`, undefined, 'listQueues').then((page) => {
    /* The serving engine's envelope carries the rows under `queues`; the older
     * mock family used `data`. Accept both — .data.map on undefined threw here,
     * which blanked the Concierge (and every join built on this call). */
    /* Annotated, not inferred: `(page as any).data` made `rows` — and therefore
     * the returned `data` — `any`, so every consumer of this op silently lost
     * QueueSummary typing. That is how `queues.queues` survived review in
     * regional-calendar-data.ts: on `any` there is nothing left to catch it. */
    const envelope = page as { data?: QueueRowWire[]; queues?: QueueRowWire[] };
    const rows: QueueRowWire[] = envelope.data ?? envelope.queues ?? [];
    return { ...page, data: rows.map(normalizeQueueSummary) };
  }));
export const listQueueTypes = () =>
  tag("listQueueTypes", apiRaw<{ types: QueueType[] }>("/v1/admin/queues/types"));

/** The ONE canonical "content" shape for both create and update — settings_json
 *  PAYLOAD, not `queues` table columns. Field-for-field match of
 *  `QueueContentFields` in `@v0y4ge/api-contracts` (packages/api-contracts/src/
 *  queue.ts), which the real worker (`workers/queue/src/routes/queues.ts`) and
 *  `@v0y4ge/api-mocks` both fold into `settings_json` via a read-modify-write
 *  merge. Sent RAW camelCase over the wire via `apiRaw` — not through the
 *  api-client's snake_case translation layer. */
export interface QueueContentPatch {
  typeId?: string;
  ticketPrefix?: string;
  slaTargetMins?: number;
  needed?: number;
  journey?: QFFlow | null;
  journeySource?: string;
  heroImage?: string | null;
  flowId?: string | null;
  hours?: QueueHours[];
  assignedStaff?: QueueStaffRef[];
  services?: string[];
  serviceSelection?: QueueServiceSel[];
  resources?: string[];
  autoOffOnConflict?: boolean;
}
export interface QueueCreatePayload extends Pick<QueueContentPatch, "typeId" | "ticketPrefix" | "slaTargetMins" | "services" | "flowId"> {
  name: string;
  locationId: string;
  locationName?: string;
}
export type QueueUpdatePayload = QueueContentPatch & { name?: string };

export const createQueue = (input: QueueCreatePayload) =>
  tag("createQueue", apiRaw<QueueSummary>("/v1/admin/queues", json(input), 'createQueue').then(normalizeQueueSummary));
export const updateQueue = (id: string, patch: QueueUpdatePayload) =>
  tag("updateQueue", apiRaw<QueueSummary>(`/v1/admin/queues/${id}`, { ...json(patch), method: "PATCH" }, 'updateQueue').then(normalizeQueueSummary));
export const setQueueStatus = (id: string, status: QueueStatus) =>
  tag("setQueueStatus", apiRaw<QueueSummary>(`/v1/admin/queues/${id}/status`, json({ status }), 'setQueueStatus').then(normalizeQueueSummary));
/** Take an assigned staffer off the live queue / put them back on (manual or on a calendar conflict). */
export const setQueueStaffStatus = (queueId: string, staffId: string, onQueue: boolean, reason?: string) =>
  tag("setQueueStaffStatus", apiRaw<QueueSummary>(`/v1/admin/queues/${queueId}/staff/${staffId}/status`, json({ onQueue, reason }), 'setQueueStaffStatus').then(normalizeQueueSummary));
/** Temporarily mark one of the queue's services unavailable (or available again) without detaching it. */
export const setQueueServiceAvailability = (queueId: string, service: string, available: boolean) =>
  tag("setQueueServiceAvailability", apiRaw<QueueSummary>(`/v1/admin/queues/${queueId}/services/availability`, json({ service, available }), 'setQueueServiceAvailability').then(normalizeQueueSummary));
/** Temporarily mark one of the queue's resources (desk / room / equipment) out of action, or back in. */
export const setQueueResourceAvailability = (queueId: string, resource: string, available: boolean) =>
  tag("setQueueResourceAvailability", apiRaw<QueueSummary>(`/v1/admin/queues/${queueId}/resources/availability`, json({ resource, available }), 'setQueueResourceAvailability').then(normalizeQueueSummary));

/* ── Live board + entry actions ─────────────────────────────────────── */
export const getQueueBoard = (queueId: string) =>
  tag("getQueueBoard", apiRaw<QueueBoardWire>(`/v1/admin/queues/${queueId}/board`, undefined, 'getQueueBoard').then(normalizeQueueBoard));
export const addQueueEntry = (queueId: string, input: { name: string; phone?: string; channel?: MsgChannel; party?: number; service?: string | null; priority?: boolean; answers?: { q: string; a: string }[]; preferredStaff?: { id: string; name: string } | null }) =>
  tag("addQueueEntry", apiRaw<QueueEntry>(`/v1/admin/queues/${queueId}/entries`, json(input), 'addQueueEntry'));
/** Single entry-state transition op: action ∈ call | recall | serve | complete | no-show | remove | prioritize | readd — plus plain field patches.
 *  `readd` puts a finished (no-show / left) customer back in THIS queue as waiting; `readdAt` picks
 *  front (they already waited) or back. A previously-called customer comes back with `calledBefore`. */
export const patchQueueEntry = (queueId: string, entryId: string, patch: { action?: string; readdAt?: "front" | "back" } & Partial<QueueEntry>) =>
  tag("patchQueueEntry", apiRaw<QueueEntry>(`/v1/admin/queues/${queueId}/entries/${entryId}`, { ...json(patch), method: "PATCH" }, 'patchQueueEntry'));
/** `position` (1-based slot among the target's waiting line, drag-drop) is optional — absent = back of
 *  the queue. The backend owns final ordering (priority entries still sort first). */
export const transferQueueEntry = (queueId: string, entryId: string, toQueueId: string, opts?: { position?: number }) =>
  tag("transferQueueEntry", apiRaw<QueueEntry>(`/v1/admin/queues/${queueId}/entries/${entryId}/transfer`, json({ toQueueId, position: opts?.position }), 'transferQueueEntry'));

/* ── Appointments booked FROM the concierge (REQ-040) — a future service
      appointment for a customer at the counter (or in the queue). The
      post-booking confirmation journey is a declared seam: the standard
      appointment-confirmation flow (requirements TBD) picks up from `status:
      "booked"`; the concierge only shows "confirmation to follow". ── */
export interface ApptSlot { start: string; end: string; staffId: string; staffName: string; }
export interface BookedAppointment {
  id: string; ref: string; date: string; start: string; end: string;
  service: string; staffId: string | null; staffName: string | null;
  locationId: string | null; customer: { name: string; phone: string; channel: MsgChannel };
  /** "booked" now; the standard confirmation flow (not yet specced) advances it. */
  status: string;
}
/** Bookable slots for one service + local date at a location. Staff filtering is client-side
 *  (each slot names its staffer) — scope params: locationId + service + date re-cook the grid.
 *  CONVERGED 2026-07-26 (APT-02b): both fns now call the CANONICAL appointments
 *  family (/v1/admin/appointments/*, REQ-089); the REQ-040 queues-namespace
 *  paths retire as aliases. Exports/types unchanged — the console UX is untouched. */
export const listApptSlots = (params: { service: string; date: string; locationId?: string | null }) =>
  tag("listApptSlots", apiRaw<{ date: string; service: string; slots: ApptSlot[] }>(`/v1/admin/appointments/slots${q({ service: params.service, date: params.date, locationId: params.locationId })}`, undefined, 'listApptSlots'));
export const bookAppointment = (input: { service: string; date: string; start: string; end: string; staffId?: string | null; staffName?: string | null; locationId?: string | null; customer: { name: string; phone: string; channel: MsgChannel }; queueId?: string | null; entryId?: string | null; note?: string | null }) =>
  tag("bookAppointment", apiRaw<BookedAppointment>("/v1/admin/appointments", json(input), 'bookAppointment'));

/* ── 2-way messaging (SMS / WhatsApp thread per entry) ──────────────── */
export const listQueueMessages = (queueId: string, entryId: string) =>
  tag("listQueueMessages", apiRaw<{ messages: QueueMessage[] }>(`/v1/admin/queues/${queueId}/entries/${entryId}/messages`, undefined, 'listQueueMessages'));
export const sendQueueMessage = (queueId: string, entryId: string, text: string) =>
  tag("sendQueueMessage", apiRaw<QueueMessage>(`/v1/admin/queues/${queueId}/entries/${entryId}/messages`, json({ text }), 'sendQueueMessage'));
/** Broadcast one message to everyone active in the queue (waiting / called / serving), each via their
 *  chosen channel. `opts.service` / `opts.resource` narrow to affected entries. `{{tokens}}` (queue_name,
 *  location, service, resource, position, est_wait) resolve PER RECIPIENT at send time. */
export const broadcastQueueMessage = (queueId: string, text: string, opts?: { service?: string | null; resource?: string | null }) =>
  tag("broadcastQueueMessage", apiRaw<{ sent: number }>(`/v1/admin/queues/${queueId}/broadcast`, json({ text, service: opts?.service ?? undefined, resource: opts?.resource ?? undefined }), 'broadcastQueueMessage'));
/** Move every waiting/called entry to another queue (serving entries finish where they are).
 *  Optional `text` notifies each moved customer (same `{{tokens}}`, plus {{to_queue}}). */
export const transferAllQueueEntries = (queueId: string, toQueueId: string, opts?: { text?: string | null }) =>
  tag("transferAllQueueEntries", apiRaw<{ moved: number; notified: number }>(`/v1/admin/queues/${queueId}/transfer-all`, json({ toQueueId, text: opts?.text ?? undefined }), 'transferAllQueueEntries'));

/* ── Queue flows (visual designer library) ──────────────────────────── */
export const listQueueFlows = () =>
  tag("listQueueFlows", apiRaw<{ flows: QueueFlowRecord[] }>("/v1/admin/queue-flows"));
export const saveQueueFlow = (flow: Omit<QueueFlowRecord, "usedBy" | "updatedAt"> & { usedBy?: number }) =>
  tag("saveQueueFlow", apiRaw<QueueFlowRecord>("/v1/admin/queue-flows", json(flow), 'saveQueueFlow'));
export const deleteQueueFlow = (id: string) =>
  tag("deleteQueueFlow", apiRaw<{ ok: boolean }>(`/v1/admin/queue-flows/${id}`, { method: "DELETE" }, 'deleteQueueFlow'));

/* ── Health & recommendations ──────────────────────────────────────── */
export const getQueueHealth = (range: string, locationId?: string | null, queueId?: string | null) =>
  tag("getQueueHealth", apiRaw<QueueHealth>(`/v1/admin/queues/health${q({ range, locationId, queueId })}`, undefined, 'getQueueHealth'));
export const getQueueRecommendation = (input: { alertId?: string; context: string }) =>
  tag("getQueueRecommendation", apiRaw<{ recommendation: QueueRecommendation }>("/v1/admin/queues/recommendations", json(input), 'getQueueRecommendation'));

/* Display boards (public "now serving" displays). */
export const listMessageBoards = () =>
  tag("listMessageBoards", apiRaw<{ boards: MessageBoard[] }>("/v1/admin/queues/message-boards"));
export const saveMessageBoard = (board: Omit<MessageBoard, "updatedAt"> & { updatedAt?: string }) =>
  tag("saveMessageBoard", apiRaw<MessageBoard>("/v1/admin/queues/message-boards", json(board), 'saveMessageBoard'));
export const deleteMessageBoard = (id: string) =>
  tag("deleteMessageBoard", apiRaw<{ ok: boolean }>(`/v1/admin/queues/message-boards/${id}`, { method: "DELETE" }, 'deleteMessageBoard'));
export const getPublicDisplay = (boardId: string, token?: string | null) =>
  tag("getPublicDisplay", apiRaw<PublicDisplay>(`/v1/public/queue-boards/${boardId}${q({ t: token })}`, undefined, 'getPublicDisplay'));
/** Mint the board's signed, expiring, location-bound display token plus the
 *  relative display path. The lobby display surface is token-authorized, never
 *  anonymous, so every affordance that OPENS or SHARES a board's display URL
 *  has to mint first -- a tokenless `/queue/display?b=<id>` always fails
 *  closed with the 403 banner. Caller must hold `queues.publish`. */
export const mintBoardDisplayUrl = (boardId: string, expiresInDays?: number) =>
  tag("mintBoardDisplayUrl", apiRaw<{ boardId: string; locationId: string | null; token: string; expiresAt: string; displayPath: string }>(
    `/v1/admin/queues/message-boards/${boardId}/display-url`,
    json(expiresInDays === undefined ? {} : { expiresInDays }), 'mintBoardDisplayUrl',
  ));

/* Archive (all queuers, admin's active locations). */
export const listQueueArchive = (locationId?: string | null, range?: string) =>
  tag("listQueueArchive", apiRaw<{ entries: ArchiveEntry[] }>(`/v1/admin/queues/archive${q({ locationId, range })}`, undefined, 'listQueueArchive'));

/* ── Public (customer join / status via QR link) ────────────────────── */
export const getPublicQueue = (queueId: string, entryId?: string | null) =>
  tag("getPublicQueue", apiRaw<PublicQueueView>(`/v1/public/queues/${queueId}${q({ entryId })}`, undefined, 'getPublicQueue'));
export const joinPublicQueue = (queueId: string, input: { name: string; phone: string; channel: MsgChannel; party?: number; service?: string | null; email?: string | null; locationId?: string | null; answers?: { q: string; a: string }[] }) =>
  tag("joinPublicQueue", apiRaw<{ entryId: string; position: number; estWaitMins: number; ticket?: string }>(`/v1/public/queues/${queueId}/join`, json(input), 'joinPublicQueue'));
export const leavePublicQueue = (queueId: string, entryId: string) =>
  tag("leavePublicQueue", apiRaw<{ ok: boolean }>(`/v1/public/queues/${queueId}/leave`, json({ entryId }), 'leavePublicQueue'));
/** Customer → host message from the Waiting-in-queue page (2-way; replies arrive via SMS/WhatsApp + the same thread). */
export const sendPublicQueueMessage = (queueId: string, entryId: string, text: string) =>
  tag("sendPublicQueueMessage", apiRaw<{ ok: boolean }>(`/v1/public/queues/${queueId}/messages`, json({ entryId, text }), 'sendPublicQueueMessage'));
/** CSAT submission from the How-did-we-do page. */
export const submitPublicQueueSurvey = (queueId: string, entryId: string, surveyId: string, answers: { questionId: string; value: string | number }[]) =>
  tag("submitPublicQueueSurvey", apiRaw<{ ok: boolean }>(`/v1/public/queues/${queueId}/survey`, json({ entryId, surveyId, answers }), 'submitPublicQueueSurvey'));
